import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Recipient } from '../interfaces/recipient';
import { Donor } from '../interfaces/donor';
import { Pair } from '../interfaces/pair';

// A related participant (pair partner or transplant partner) returned for the historical screen.
export interface HistoricalPartnerRef {
  type: 'recipient' | 'donor';
  enisNumber: string;
  centerCode: string | null;
  id?: string; // Only present when the current user may navigate to this partner's history
  canLink: boolean;
}

export interface HistoricalRelations {
  pairedWith: HistoricalPartnerRef[];
  transplantedWith: HistoricalPartnerRef | null;
}

@Injectable({
  providedIn: 'root'
})
export class HistoricalService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Get pair + transplant relationships for a single historical participant
  getRelations(type: 'recipient' | 'donor', enisNumber: string): Observable<HistoricalRelations> {
    return this.http.get<HistoricalRelations>(`${this.apiUrl}/participants/historical/relations/${type}/${enisNumber}`);
  }

  // Get historical recipients
  getHistoricalRecipients(transplanted?: boolean): Observable<Recipient[]> {
    let params = new HttpParams();
    if (transplanted !== undefined) {
      params = params.set('transplanted', transplanted.toString());
    }
    return this.http.get<Recipient[]>(`${this.apiUrl}/participants/historical/recipients`, { params });
  }

  // Get historical donors
  getHistoricalDonors(transplanted?: boolean): Observable<Donor[]> {
    let params = new HttpParams();
    if (transplanted !== undefined) {
      params = params.set('transplanted', transplanted.toString());
    }
    return this.http.get<Donor[]>(`${this.apiUrl}/participants/historical/donors`, { params });
  }

  // Get historical pairs
  getHistoricalPairs(transplanted?: boolean): Observable<Pair[]> {
    let params = new HttpParams();
    if (transplanted !== undefined) {
      params = params.set('transplanted', transplanted.toString());
    }
    return this.http.get<Pair[]>(`${this.apiUrl}/pairs/historical`, { params });
  }

  // Mark recipient as historical
  markRecipientAsHistorical(recipientId: string, reason: string): Observable<Recipient> {
    return this.http.post<Recipient>(`${this.apiUrl}/participants/historical/recipient/${recipientId}`, { reason });
  }

  // Mark donor as historical
  markDonorAsHistorical(donorId: string, reason: string): Observable<Donor> {
    return this.http.post<Donor>(`${this.apiUrl}/participants/historical/donor/${donorId}`, { reason });
  }

  // Mark pair as historical
  markPairAsHistorical(pairId: string, reason: string): Observable<Pair> {
    return this.http.post<Pair>(`${this.apiUrl}/pairs/historical/${pairId}`, { reason });
  }

  // Unmark recipient as historical
  unmarkRecipientAsHistorical(recipientId: string): Observable<Recipient> {
    return this.http.delete<Recipient>(`${this.apiUrl}/participants/historical/recipient/${recipientId}`);
  }

  // Unmark donor as historical
  unmarkDonorAsHistorical(donorId: string): Observable<Donor> {
    return this.http.delete<Donor>(`${this.apiUrl}/participants/historical/donor/${donorId}`);
  }

  // Unmark pair as historical
  unmarkPairAsHistorical(pairId: string): Observable<Pair> {
    return this.http.delete<Pair>(`${this.apiUrl}/pairs/historical/${pairId}`);
  }
}
